import { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { RefreshCw, ArrowDownNarrowWide, CalendarArrowDown, Users, BanknoteX, Archive, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
// (Card imports moved to RegistrationCard component)
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import type { PromotionBase } from "nautika-types";
import { RegistrationCard } from "../../registrations/components/registration-card";
import type { RegistrationCardProps } from "../../registrations/components/registration-card";
import { useRegistrationLayout } from "../hooks/useRegistrationLayout";
import { useEffect } from "react";

export function Registrations() {
  const [sortOrder, setSortOrder] = useState("recent");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive activeTab directly from URL - no useState needed
  const activeTab = searchParams.get("tab") || "all";

  // Get search state from layout context
  const { searchQuery, registerRefetch, setIsLoading } = useRegistrationLayout();

  // Update URL when tab changes (from internal tab clicks)
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  // Récupération des données via l'API
  const { data: registrationsResponse, refetch, isLoading } = api.useQuery("get", "/registrations/", {});

  // Register refetch function with layout context for refresh button
  useEffect(() => {
    registerRefetch(async () => {
      await refetch();
    });
    setIsLoading(isLoading);
  }, [refetch, isLoading, registerRefetch, setIsLoading]);

  // Calcul du "reste à payer" avec prise en compte des promotions
  const registrationsWithCalculatedData = useMemo(() => {
    const registrationsData = registrationsResponse || [];
    return registrationsData.map((registration) => {
      // Calcul du montant de la promotion
      const promotionDiscount =
        registration.promotions && registration.promotions.length > 0
          ? registration.promotions.reduce((total: number, promo: PromotionBase) => {
              if (promo.type === "percentage") {
                return total + ((registration?.subtotalAmount ?? 0) * promo.value) / 100;
              } else {
                // Type "fixed" - montant fixe en centimes, convertir en euros
                return total + promo.value / 100;
              }
            }, 0)
          : 0;

      // Calcul du montant total après promotion
      const totalAfterPromotion = (registration.subtotalAmount ?? 0) - promotionDiscount;

      // Calcul du montant total payé
      const totalPaid =
        (registration.paymentDetails.card || 0) +
        (registration.paymentDetails.ancv || 0) +
        (registration.paymentDetails.cash || 0) +
        (registration.paymentDetails.check || 0);

      return {
        ...registration,
        remainingAmount: totalAfterPromotion - totalPaid,
      };
    });
  }, [registrationsResponse]);

  // Count pending inscriptions
  const pendingCount = registrationsWithCalculatedData.filter((reg) => reg.remainingAmount > 0 && !reg.archived).length;
  // Helper: define incomplete registration
  const isIncomplete = useCallback((reg: (typeof registrationsWithCalculatedData)[number]) => {
    const priceMissing = (reg.subtotalAmount ?? 0) === 0;
    const noAdults = (reg.adults?.length ?? 0) === 0;
    return !reg.archived && (priceMissing || noAdults);
  }, []);
  const incompleteList = registrationsWithCalculatedData.filter(isIncomplete);
  const incompleteCount = incompleteList.length;

  // Filtrage et tri des inscriptions
  const filteredAndSortedRegistrations = useMemo(() => {
    let filtered = [...registrationsWithCalculatedData];

    // Filtrage par onglet
    switch (activeTab) {
      case "pending":
        filtered = filtered.filter((reg) => reg.remainingAmount > 0 && !reg.archived);
        break;
      case "incomplete":
        filtered = filtered.filter(isIncomplete);
        break;
      case "archived":
        filtered = filtered.filter((reg) => reg.archived);
        break;
      case "all":
      default:
        filtered = filtered.filter((reg) => !reg.archived);
        break;
    }

    // Filtrage par recherche (uniquement pour les onglets "all" et "pending")
    if (searchQuery.trim() && activeTab !== "archived") {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((registration) => {
        // Recherche dans les noms des parents
        const parentMatch = registration.adults.some((adult) => {
          const fullName = `${adult.firstName} ${adult.lastName}`.toLowerCase();
          return fullName.includes(query) || (adult.phone || "").includes(query) || (adult.email || "").toLowerCase().includes(query);
        });

        // Recherche dans les noms des enfants
        const childMatch = registration.children.some((child) => {
          const fullName = `${child.firstName} ${child.lastName}`.toLowerCase();
          return fullName.includes(query);
        });

        // Recherche dans le montant total
        const totalPaid =
          (registration.paymentDetails.card || 0) +
          (registration.paymentDetails.ancv || 0) +
          (registration.paymentDetails.cash || 0) +
          (registration.paymentDetails.check || 0);
        const amountMatch = totalPaid.toString().includes(query) || registration.remainingAmount.toString().includes(query);

        return parentMatch || childMatch || amountMatch;
      });
    }

    // Tri
    const applySecondarySort = (list: typeof filtered) => {
      switch (sortOrder) {
        case "recent":
          list.sort((a, b) => {
            const dateA = typeof a.createdAt === "string" ? new Date(a.createdAt) : new Date();
            const dateB = typeof b.createdAt === "string" ? new Date(b.createdAt) : new Date();
            return dateB.getTime() - dateA.getTime();
          });
          break;
        case "amount":
          list.sort((a, b) => b.remainingAmount - a.remainingAmount);
          break;
      }
    };

    if (activeTab === "pending") {
      // Prioriser celles avec un prix défini, puis appliquer le tri choisi
      filtered.sort((a, b) => {
        const aHasPrice = (a.subtotalAmount ?? 0) > 0 ? 1 : 0;
        const bHasPrice = (b.subtotalAmount ?? 0) > 0 ? 1 : 0;
        return bHasPrice - aHasPrice;
      });
      applySecondarySort(filtered);
    } else {
      applySecondarySort(filtered);
    }

    return filtered;
  }, [registrationsWithCalculatedData, activeTab, searchQuery, sortOrder, isIncomplete]);

  // Fonctions d'affichage
  // Card utilities moved into RegistrationCard component

  // Mutations pour archiver et désarchiver une inscription
  const archiveMutation = api.useMutation("delete", "/registrations/{id}/archive");
  const unarchiveMutation = api.useMutation("patch", "/registrations/{id}");

  // Fonction pour archiver une inscription
  const handleArchiveRegistration = async (registrationId: string) => {
    try {
      await archiveMutation.mutateAsync({
        params: {
          path: { id: registrationId },
        },
      });
      // Recharger les données après archivage
      refetch();
    } catch (error) {
      console.error("Erreur lors de l'archivage:", error);
    }
  };
  const handleUnarchiveRegistration = async (registrationId: string) => {
    try {
      // Find the registration to get its current payment details
      const registration = registrationsWithCalculatedData.find((reg) => reg.id === registrationId);
      if (!registration) return;

      await unarchiveMutation.mutateAsync({
        params: { path: { id: registrationId } },
        body: {
          archived: false,
          paymentDetails: registration.paymentDetails,
        },
      });
      // Recharger les données après désarchivage
      refetch();
    } catch (error) {
      console.error("Erreur lors de la désarchivation:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">Chargement des inscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mb-4">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="all" className="flex items-center gap-2 px-4 text-sm">
                  <Users size={16} className="shrink-0" />
                  <span className="hidden lg:inline">Toutes les inscriptions</span>
                </TabsTrigger>
                <TabsTrigger value="pending" className="flex items-center gap-2 px-4 text-sm">
                  <BanknoteX size={16} className="shrink-0" />
                  <div className="flex items-center">
                    <span className="hidden lg:inline">En attente de règlement</span>
                    {pendingCount > 0 && <Badge className="ml-2 h-5 min-w-5 rounded-full bg-red-100 px-1 text-red-700">{pendingCount}</Badge>}
                  </div>
                </TabsTrigger>
                <TabsTrigger value="incomplete" className="flex items-center gap-2 px-4 text-sm">
                  <AlertTriangle size={16} className="shrink-0" />
                  <div className="flex items-center">
                    <span className="hidden lg:inline">Inscriptions à compléter</span>
                    {incompleteCount > 0 && <Badge className="ml-2 h-5 min-w-5 rounded-full bg-orange-100 px-1 text-orange-700">{incompleteCount}</Badge>}
                  </div>
                </TabsTrigger>
                <TabsTrigger value="archived" className="flex items-center gap-2 px-4 text-sm">
                  <Archive size={16} className="shrink-0" />
                  <span className="hidden lg:inline">Archivées</span>
                </TabsTrigger>
              </TabsList>

              <div className="flex items-center">
                <span className="mr-2 text-sm text-gray-500">Trier par</span>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="h-9 w-48 bg-white">
                    <SelectValue placeholder="Plus récent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">
                      <span className="inline-flex items-center gap-2">
                        <CalendarArrowDown size={16} className="text-gray-500" />
                        Plus récent
                      </span>
                    </SelectItem>
                    <SelectItem value="amount">
                      <span className="inline-flex items-center gap-2">
                        <ArrowDownNarrowWide size={16} className="text-gray-500" />
                        Montant à payer
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <TabsContent value={activeTab} className="mt-4">
              {/* Special layouts per tab */}
              {activeTab === "all" && (
                <>
                  {/* Mode recherche : afficher un seul grid combiné (inclus les incomplètes) */}
                  {searchQuery.trim() ? (
                    (() => {
                      const list = filteredAndSortedRegistrations;
                      if (list.length === 0) {
                        return (
                          <div className="flex h-64 items-center justify-center">
                            <div className="text-center">
                              <p className="text-xl font-medium text-gray-900">Aucune inscription trouvée pour cette recherche</p>
                              <p className="mt-1 text-sm text-gray-500">Essayez avec d'autres mots-clés.</p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {list.map((registration) => (
                            <RegistrationCard
                              key={registration.id}
                              registration={registration as RegistrationCardProps["registration"]}
                              activeTab={activeTab}
                              onArchive={handleArchiveRegistration}
                              onUnarchive={handleUnarchiveRegistration}
                              archivePending={archiveMutation.isPending}
                              unarchivePending={unarchiveMutation.isPending}
                              onClick={() => navigate(`/dash/registrations/${registration.id}?tab=${activeTab}`)}
                            />
                          ))}
                        </div>
                      );
                    })()
                  ) : (
                    <>
                      {/* Section Incomplete (limit 3) -- //? Désactiver car redondant avec l'onglet correspondant */}
                      {incompleteCount > 0 && (
                        <div className="mb-6 hidden">
                          <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Inscriptions à compléter</h2>
                            {incompleteCount > 3 && (
                              <div className="text-xs text-gray-600">
                                <Button variant="link" className="ml-1 h-auto p-0 text-xs" onClick={() => handleTabChange("incomplete")}>
                                  Voir {incompleteCount - 3} autre{incompleteCount - 3 > 1 ? "s" : ""} inscription{incompleteCount - 3 > 1 ? "s" : ""}{" "}
                                  incomplète
                                  {incompleteCount - 3 > 1 ? "s" : ""}
                                </Button>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {incompleteList.slice(0, 3).map((registration) => (
                              <RegistrationCard
                                key={registration.id}
                                registration={registration as RegistrationCardProps["registration"]}
                                activeTab={activeTab}
                                onArchive={handleArchiveRegistration}
                                onUnarchive={handleUnarchiveRegistration}
                                archivePending={archiveMutation.isPending}
                                unarchivePending={unarchiveMutation.isPending}
                                onClick={() => navigate(`/dash/registrations/${registration.id}?tab=${activeTab}`)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Toutes les inscriptions (hors incomplètes déjà affichées) */}
                      <div>
                        <div className="mb-3 hidden items-center justify-between">
                          <h2 className="text-lg font-semibold text-gray-900">Toutes les inscriptions</h2>
                        </div>
                        {(() => {
                          const list = filteredAndSortedRegistrations.filter((r) => !isIncomplete(r));
                          if (list.length === 0) {
                            return (
                              <div className="flex h-64 items-center justify-center">
                                <div className="text-center">
                                  <p className="text-xl font-medium text-gray-900">Aucune inscription pour le moment.</p>
                                  <p className="mt-1 text-sm text-gray-500">Créez votre première inscription pour commencer.</p>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                              {list.map((registration) => (
                                <RegistrationCard
                                  key={registration.id}
                                  registration={registration as RegistrationCardProps["registration"]}
                                  activeTab={activeTab}
                                  onArchive={handleArchiveRegistration}
                                  onUnarchive={handleArchiveRegistration}
                                  archivePending={archiveMutation.isPending}
                                  unarchivePending={unarchiveMutation.isPending}
                                  onClick={() => navigate(`/dash/registrations/${registration.id}?tab=${activeTab}`)}
                                />
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </>
              )}

              {activeTab === "pending" &&
                (filteredAndSortedRegistrations.length === 0 ? (
                  <div className="flex h-64 items-center justify-center">
                    <div className="text-center">
                      <p className="text-xl font-medium text-gray-900">Aucune inscription en attente de paiement</p>
                      <p className="mt-1 text-sm text-gray-500">Lorsqu'une inscription n'a pas été entièrement réglée, elle apparaîtra ici.</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAndSortedRegistrations.map((registration) => (
                      <RegistrationCard
                        key={registration.id}
                        registration={registration as RegistrationCardProps["registration"]}
                        activeTab={activeTab}
                        onArchive={handleArchiveRegistration}
                        onUnarchive={handleUnarchiveRegistration}
                        archivePending={archiveMutation.isPending}
                        unarchivePending={unarchiveMutation.isPending}
                        onClick={() => navigate(`/dash/registrations/${registration.id}?tab=${activeTab}`)}
                      />
                    ))}
                  </div>
                ))}

              {activeTab === "incomplete" &&
                (() => {
                  const list = filteredAndSortedRegistrations; // déjà filtré + recherche
                  if (list.length === 0) {
                    return (
                      <div className="flex h-64 items-center justify-center">
                        <div className="text-center">
                          <p className="text-xl font-medium text-gray-900">
                            {searchQuery.trim() ? "Aucune inscription trouvée pour cette recherche" : "Aucune inscription à compléter"}
                          </p>
                          <p className="mt-1 text-sm text-gray-500">
                            {searchQuery.trim() ? "Essayez avec d'autres mots-clés." : "Toutes les inscriptions semblent complètes."}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {list.map((registration) => (
                        <RegistrationCard
                          key={registration.id}
                          registration={registration as RegistrationCardProps["registration"]}
                          activeTab={activeTab}
                          onArchive={handleArchiveRegistration}
                          onUnarchive={handleUnarchiveRegistration}
                          archivePending={archiveMutation.isPending}
                          unarchivePending={unarchiveMutation.isPending}
                          onClick={() => navigate(`/dash/registrations/${registration.id}?tab=${activeTab}`)}
                        />
                      ))}
                    </div>
                  );
                })()}

              {activeTab === "archived" &&
                (filteredAndSortedRegistrations.length === 0 ? (
                  <div className="flex h-64 items-center justify-center">
                    <div className="text-center">
                      <p className="text-xl font-medium text-gray-900">Aucune inscription archivée</p>
                      <p className="mt-1 text-sm text-gray-500">Vous pouvez archiver une inscription depuis le tableau des inscriptions.</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAndSortedRegistrations.map((registration) => (
                      <RegistrationCard
                        key={registration.id}
                        registration={registration as RegistrationCardProps["registration"]}
                        activeTab={activeTab}
                        onArchive={handleArchiveRegistration}
                        onUnarchive={handleUnarchiveRegistration}
                        archivePending={archiveMutation.isPending}
                        unarchivePending={unarchiveMutation.isPending}
                        onClick={() => navigate(`/dash/registrations/${registration.id}?tab=${activeTab}`)}
                      />
                    ))}
                  </div>
                ))}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </>
  );
}
